import { useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Lock, Unlock } from "lucide-react";

const ALL_HUBS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "smart_office", label: "Smart Office" },
  { key: "comms", label: "Comms Centre" },
  { key: "hr", label: "HR Hub" },
  { key: "logistics", label: "Logistics" },
  { key: "travelling", label: "Travelling Admin" },
  { key: "request_service", label: "Request Service" },
  { key: "vetting", label: "Smart Vetting" },
  { key: "monitoring", label: "Monitoring Centre" },
  { key: "innovation", label: "Innovation Centre" },
  { key: "learning", label: "Learning Hub" },
  { key: "courses", label: "Courses" },
  { key: "knowledge", label: "Knowledge Centre" },
  { key: "agent", label: "Agent Support" },
  { key: "ims", label: "IMS Hub" },
  { key: "cyber_monitor", label: "Cyber Monitor" },
  { key: "evet", label: "eVET Hub" },
  { key: "smartpay", label: "SmartPay" },
  { key: "erp", label: "ERP Hub" },
  { key: "alerts", label: "Alerts Hub" },
  { key: "rewards", label: "Rewards Centre" },
  { key: "aipps", label: "Aipps Credits" },
];

export default function HubSelector({ selectedPlan, initialHubs, onChange }) {
  const planHubs = selectedPlan?.hubs || {};
  const [hubs, setHubs] = useState(() => {
    if (initialHubs) return { ...initialHubs };
    // Default: enable all hubs included in the plan
    const defaults = {};
    for (const hub of ALL_HUBS) {
      defaults[hub.key] = planHubs[hub.key] !== false;
    }
    return defaults;
  });

  const toggleHub = (key) => {
    // Don't allow toggling hubs not in the plan
    if (planHubs[key] === false) return;

    const updated = { ...hubs, [key]: !hubs[key] };
    setHubs(updated);
    if (onChange) onChange(updated);
  };

  const selectedCount = Object.values(hubs).filter(Boolean).length;
  const planHubCount = Object.values(planHubs).filter(Boolean).length;
  const isAllSelected = selectedCount >= planHubCount;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium">Select Your Hubs</h4>
          <p className="text-sm text-muted-foreground">
            Choose which tools your organisation needs
          </p>
        </div>
        <Badge variant="outline" className="text-sm">
          {selectedCount} of {planHubCount} selected
        </Badge>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {ALL_HUBS.map((hub) => {
          const inPlan = planHubs[hub.key] !== false;
          const isEnabled = hubs[hub.key] !== false;

          return (
            <div
              key={hub.key}
              className={`flex items-center gap-2 p-2 rounded border transition-colors ${
                !inPlan
                  ? "bg-muted/30 opacity-50 border-dashed"
                  : isEnabled
                  ? "bg-primary/5 border-primary/20"
                  : "bg-background"
              }`}
            >
              {inPlan ? (
                <Switch
                  checked={isEnabled}
                  onCheckedChange={() => toggleHub(hub.key)}
                  id={`hub-${hub.key}`}
                />
              ) : (
                <Lock className="w-4 h-4 text-muted-foreground" />
              )}
              <Label
                htmlFor={`hub-${hub.key}`}
                className={`text-sm cursor-pointer flex-1 ${
                  inPlan ? "" : "text-muted-foreground"
                }`}
              >
                {hub.label}
              </Label>
            </div>
          );
        })}
      </div>
    </div>
  );
}
